import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faBasketball,
    faBuilding,
    faGaugeHigh,
    faGear,
    faMicrochip,
    faMoon,
    faReceipt,
    faRightFromBracket,
    faRightLeft,
    faSun,
    faUserShield,
    faXmark,
} from '@fortawesome/free-solid-svg-icons'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { useIsMobile } from '../lib/useMediaQuery'
import { useAuth } from '../features/auth/AuthProvider'
import { useAccount } from '../features/auth/AccountProvider'
import { Logo } from './Logo'

type NavItem = { to: string; label: string; icon: IconDefinition }

const navItems: NavItem[] = [
    { to: '/dashboard', label: 'Dashboard', icon: faGaugeHigh },
    { to: '/games', label: 'Games', icon: faBasketball },
    // faClockRotateLeft would be the obvious icon, but Replay owns it.
    { to: '/history', label: 'History', icon: faReceipt },
]

// Engine metrics section (engine-metrics plan §3.1) — appended to the primary
// nav only for admins, ahead of the Admin console.
const engineNavItem: NavItem = { to: '/engine', label: 'Engine', icon: faMicrochip }

// Admin console — appended to the primary nav (top strip + mobile bottom bar)
// only for admins. Previously lived in the account menu.
const adminNavItem: NavItem = { to: '/admin', label: 'Admin', icon: faUserShield }

type Theme = 'light' | 'dark' | 'system'
const STORAGE_KEY = 'tradeski.appearance'
type Appearance = { theme: Theme; density: 'compact' | 'comfortable' }

// The page title as index.html shipped it; an organization's name is
// prefixed while acting for one, so the tab itself says whose money.
const BASE_TITLE = document.title

const resolveTheme = (t: Theme): 'light' | 'dark' =>
    t === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light'
        : t

const loadAppearance = (): Appearance => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        return raw
            ? { theme: 'system', density: 'comfortable', ...JSON.parse(raw) }
            : { theme: 'system', density: 'comfortable' }
    } catch {
        return { theme: 'system', density: 'comfortable' }
    }
}

const emailInitials = (email: string | undefined): string =>
    (email ?? '?').split('@')[0].slice(0, 2).toUpperCase()

// "Cloud Compass LLC" → "CC"; a one-word name → its first two letters.
const nameInitials = (name: string): string => {
    const words = name.trim().split(/\s+/).filter(Boolean)
    const letters = words.length >= 2 ? words[0][0] + words[1][0] : (words[0] ?? '?').slice(0, 2)
    return letters.toUpperCase()
}

export const AppShell = ({ children }: { children: ReactNode }) => {
    const { user, logout } = useAuth()
    const { accounts, active, isDelegated, switchTo, notice, dismissNotice } = useAccount()
    const isMobile = useIsMobile()
    const [resolved, setResolved] = useState<'light' | 'dark'>(
        () => (document.documentElement.dataset.theme as 'light' | 'dark') || 'light',
    )
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setResolved((document.documentElement.dataset.theme as 'light' | 'dark') || 'light')
        })
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme'],
        })
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (!menuOpen) return
        const onPointerDown = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setMenuOpen(false)
            }
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMenuOpen(false)
        }
        document.addEventListener('mousedown', onPointerDown)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onPointerDown)
            document.removeEventListener('keydown', onKey)
        }
    }, [menuOpen])

    useEffect(() => {
        document.title = isDelegated ? `${active.displayName} · Tradeski` : BASE_TITLE
        return () => {
            document.title = BASE_TITLE
        }
    }, [isDelegated, active.displayName])

    const toggleTheme = useCallback(() => {
        const current = loadAppearance()
        const nextResolved = resolveTheme(current.theme) === 'dark' ? 'light' : 'dark'
        const next: Appearance = { ...current, theme: nextResolved }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
        document.documentElement.dataset.theme = nextResolved
    }, [])

    const initials = isDelegated ? nameInitials(active.displayName) : emailInitials(user?.email)
    const navLinks = user?.roles.includes('Admin')
        ? [...navItems, engineNavItem, adminNavItem]
        : navItems
    const otherAccounts = accounts.filter((a) => a.id !== active.id)

    return (
        <div className="relative min-h-full flex flex-col ambient-blobs overflow-hidden">
            <div className="absolute inset-0 -z-10 court-pattern" aria-hidden="true" />

            <header className="sticky top-0 z-30 border-b border-base backdrop-blur-xl">
                <div
                    className="absolute inset-0 -z-10"
                    style={{
                        background:
                            'linear-gradient(180deg, color-mix(in srgb, var(--surface-base) 92%, transparent) 0%, color-mix(in srgb, var(--surface-base) 75%, transparent) 100%)',
                    }}
                    aria-hidden="true"
                />
                <div className="mx-auto max-w-[1500px] px-4 sm:px-6 py-3 flex items-center justify-between gap-3 sm:gap-6">
                    <div className="flex items-center gap-3 sm:gap-6 min-w-0">
                        <NavLink
                            to="/dashboard"
                            className="focusable rounded-md inline-flex items-center"
                        >
                            <Logo className="h-9" />
                        </NavLink>

                        <div className="hidden md:block h-6 w-px bg-[var(--border-strong)]" />

                        <nav className="hidden md:flex items-center gap-0.5 overflow-x-auto scrollbar-slim">
                            {navLinks.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-label={item.label}
                                    className={({ isActive }) =>
                                        ['nav-tab focusable', isActive ? 'nav-tab--active' : '']
                                            .filter(Boolean)
                                            .join(' ')
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                        </nav>
                    </div>

                    <div className="flex items-center gap-2 min-w-0">
                        {isDelegated && (
                            <span
                                className="pill pill-info min-w-0 max-w-[10rem] sm:max-w-[16rem]"
                                title={`Acting as ${active.displayName}`}
                            >
                                <FontAwesomeIcon
                                    icon={faBuilding}
                                    className="h-3 w-3 shrink-0"
                                    aria-hidden="true"
                                />
                                <span className="truncate min-w-0">{active.displayName}</span>
                            </span>
                        )}

                        <button
                            type="button"
                            onClick={toggleTheme}
                            className="btn btn-ghost btn-icon focusable"
                            aria-label={
                                resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
                            }
                            title={
                                resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
                            }
                        >
                            <FontAwesomeIcon
                                icon={resolved === 'dark' ? faSun : faMoon}
                                style={{ width: 18, height: 18 }}
                                aria-hidden="true"
                            />
                        </button>

                        <div className="relative shrink-0" ref={menuRef}>
                            <button
                                type="button"
                                onClick={() => setMenuOpen((o) => !o)}
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                                aria-label="Account menu"
                                className="focusable text-on-primary flex h-9 w-9 items-center justify-center rounded-full text-[0.75rem] font-extrabold tracking-tight transition-transform hover:scale-105"
                                style={{
                                    background:
                                        'linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%)',
                                    // A ring marks a delegated session even when the chip is
                                    // truncated to nothing on the narrowest phones.
                                    boxShadow: isDelegated
                                        ? 'var(--shadow-1), 0 0 0 2px var(--surface-base), 0 0 0 4px var(--brand-secondary)'
                                        : 'var(--shadow-1)',
                                }}
                            >
                                {initials}
                            </button>

                            {menuOpen && (
                                <div
                                    role="menu"
                                    className="border-base absolute top-full right-0 mt-2 w-64 overflow-hidden rounded-lg border"
                                    style={{
                                        background: 'var(--surface-popover)',
                                        boxShadow: 'var(--shadow-float)',
                                    }}
                                >
                                    <div className="border-divider border-b px-3 py-3">
                                        <div className="eyebrow mb-1">
                                            {isDelegated ? 'Acting as' : 'Signed in as'}
                                        </div>
                                        {isDelegated ? (
                                            <>
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="text-strong truncate text-sm font-medium">
                                                        {active.displayName}
                                                    </span>
                                                    <span className="pill pill-info shrink-0">
                                                        Organization
                                                    </span>
                                                </div>
                                                <div className="text-muted truncate text-xs mt-0.5">
                                                    {user?.email}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-strong truncate text-sm font-medium">
                                                {user?.email}
                                            </div>
                                        )}
                                    </div>
                                    {otherAccounts.length > 0 && (
                                        <div className="border-divider border-b py-1">
                                            {otherAccounts.map((account) => (
                                                <button
                                                    key={account.id}
                                                    type="button"
                                                    role="menuitem"
                                                    onClick={() => {
                                                        setMenuOpen(false)
                                                        switchTo(account.id)
                                                    }}
                                                    className="text-regular focusable flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm hover:bg-[color:var(--surface-sunken)]"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faRightLeft}
                                                        className="text-muted h-3.5 w-3.5 shrink-0"
                                                    />
                                                    <span className="truncate">
                                                        Switch to {account.displayName}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <NavLink
                                        to="/settings"
                                        onClick={() => setMenuOpen(false)}
                                        role="menuitem"
                                        className="text-regular focusable flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-[color:var(--surface-sunken)]"
                                    >
                                        <FontAwesomeIcon
                                            icon={faGear}
                                            className="text-muted h-3.5 w-3.5"
                                        />
                                        Settings
                                    </NavLink>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMenuOpen(false)
                                            logout()
                                        }}
                                        role="menuitem"
                                        className="text-regular focusable flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm hover:bg-[color:var(--surface-sunken)]"
                                    >
                                        <FontAwesomeIcon
                                            icon={faRightFromBracket}
                                            className="text-muted h-3.5 w-3.5"
                                        />
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {notice && (
                <div
                    role="status"
                    className="border-b border-base px-4 sm:px-6 py-2 flex items-center justify-between gap-3 text-sm"
                    style={{
                        background:
                            'color-mix(in srgb, var(--warning-500) 10%, var(--surface-base))',
                    }}
                >
                    <span className="text-regular">{notice}</span>
                    <button
                        type="button"
                        onClick={dismissNotice}
                        className="btn btn-ghost btn-icon focusable"
                        aria-label="Dismiss"
                    >
                        <FontAwesomeIcon icon={faXmark} style={{ width: 14, height: 14 }} />
                    </button>
                </div>
            )}

            <main
                className="flex-1 relative"
                style={
                    isMobile
                        ? { paddingBottom: 'calc(4.875rem + env(safe-area-inset-bottom))' }
                        : undefined
                }
            >
                <div className="mx-auto max-w-[1500px] px-4 sm:px-6 py-6 sm:py-8 w-full">
                    {children}
                </div>
            </main>

            {isMobile && <BottomNav items={navLinks} />}
        </div>
    )
}

/**
 * Mobile primary navigation (< 768px). A fixed bottom tab bar is the
 * thumb-reachable, always-visible pattern phones expect — far easier to hit
 * than the desktop tab strip crammed into the top header. Icon + label per
 * top-level page, a Material-style pill on the active tab, and a safe-area
 * inset so the last row clears the iPhone home indicator. The account
 * menu keeps the remaining secondary destinations (Settings / Logout).
 */
const BottomNav = ({ items }: { items: NavItem[] }) => (
    <nav className="bottom-nav" aria-label="Primary">
        {items.map((item) => (
            <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                    ['bottom-nav__item', isActive ? 'bottom-nav__item--active' : '']
                        .filter(Boolean)
                        .join(' ')
                }
            >
                <span className="bottom-nav__pill">
                    <FontAwesomeIcon
                        icon={item.icon}
                        style={{ width: '1.3rem', height: '1.3rem' }}
                        aria-hidden="true"
                    />
                </span>
                <span className="bottom-nav__label">{item.label}</span>
            </NavLink>
        ))}
    </nav>
)
